/* Dates, money, countries and languages in the student's language. Arabic, Hindi and Indonesian use the Gregorian
   calendar and Western digits (0-9), which match the codes, prices and dates students see on application portals. */
import { langDir, type Lang } from '@pathabroad/core/browser';

export const dirOf = langDir;
const LOCALES: Record<Lang, string> = { en: 'en-GB', ar: 'ar-u-ca-gregory-nu-latn', hi: 'hi-IN-u-ca-gregory-nu-latn', id: 'id-ID-u-ca-gregory-nu-latn' };
export const localeOf = (lang: Lang) => LOCALES[lang];

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/* 12 October 2027 / 12 أكتوبر 2027. Date-only values are calendar days, so they're shown without a time-zone shift. */
export function formatDate(value: string | null | undefined, lang: Lang, opts: Intl.DateTimeFormatOptions = {}): string {
  if (!value) return '';
  const dateOnly = DATE_ONLY.test(value);
  const d = new Date(dateOnly ? `${value}T00:00:00Z` : value);
  if (isNaN(d.getTime())) return value;
  return new Intl.DateTimeFormat(localeOf(lang), {
    day: 'numeric', month: 'long', year: 'numeric', ...(dateOnly ? { timeZone: 'UTC' } : {}), ...opts,
  }).format(d);
}

export function formatDateTime(value: string | null | undefined, lang: Lang): string {
  return formatDate(value, lang, { hour: 'numeric', minute: '2-digit' });
}

/* "$49" in both languages: Arabic number formatting gives "US$ 49", which reads backwards inside right-to-left text.
   The page says the prices are in US dollars. */
export function formatMoney(usd: number, _lang: Lang): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(usd);
}

export function monthNames(lang: Lang): string[] {
  const f = new Intl.DateTimeFormat(localeOf(lang), { month: 'long', timeZone: 'UTC' });
  return Array.from({ length: 12 }, (_, i) => f.format(new Date(Date.UTC(2000, i, 1))));
}

/* m:ss for countdowns */
export function clock(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/* ISO 3166-1 alpha-2 codes for places people are born in, hold a passport from or live in (plus XK, Kosovo) */
export const COUNTRY_CODES = ('AD AE AF AG AI AL AM AO AR AS AT AU AW AX AZ BA BB BD BE BF BG BH BI BJ BL BM BN BO BQ BR BS BT BW BY ' +
  'BZ CA CC CD CF CG CH CI CK CL CM CN CO CR CU CV CW CX CY CZ DE DJ DK DM DO DZ EC EE EG EH ER ES ET FI FJ FK FM FO FR GA GB ' +
  'GD GE GF GG GH GI GL GM GN GP GQ GR GT GU GW GY HK HN HR HT HU ID IE IL IM IN IO IQ IR IS IT JE JM JO JP KE KG KH KI KM KN ' +
  'KP KR KW KY KZ LA LB LC LI LK LR LS LT LU LV LY MA MC MD ME MF MG MH MK ML MM MN MO MP MQ MR MS MT MU MV MW MX MY MZ NA NC ' +
  'NE NF NG NI NL NO NP NR NU NZ OM PA PE PF PG PH PK PL PM PN PR PS PT PW PY QA RE RO RS RU RW SA SB SC SD SE SG SH SI SJ SK ' +
  'SL SM SN SO SR SS ST SV SX SY SZ TC TD TG TH TJ TK TL TM TN TO TR TT TV TW TZ UA UG US UY UZ VA VC VE VG VI VN VU WF WS XK ' +
  'YE YT ZA ZM ZW').split(' ');

const displayNames = new Map<string, Intl.DisplayNames | null>();
function names(lang: Lang, type: 'region' | 'language'): Intl.DisplayNames | null {
  const k = `${lang}:${type}`;
  if (!displayNames.has(k)) {
    try { displayNames.set(k, new Intl.DisplayNames([lang], { type, fallback: 'code' })); } catch { displayNames.set(k, null); }
  }
  return displayNames.get(k) ?? null;
}

export function countryName(code: string | null | undefined, lang: Lang): string {
  if (!code) return '';
  try { return names(lang, 'region')?.of(code.toUpperCase()) ?? code; } catch { return code; }
}

const sortedCache = new Map<Lang, Array<{ code: string; name: string }>>();
export function countryOptions(lang: Lang): Array<{ code: string; name: string }> {
  if (!sortedCache.has(lang)) {
    const collator = new Intl.Collator(lang);
    sortedCache.set(lang, COUNTRY_CODES.map(code => ({ code, name: countryName(code, lang) })).sort((a, b) => collator.compare(a.name, b.name)));
  }
  return sortedCache.get(lang)!;
}

/* first languages students most often list (ISO 639-1); the profile stores the code */
export const LANGUAGE_CODES = ['ar', 'en', 'fr', 'ur', 'hi', 'bn', 'pa', 'ta', 'te', 'ml', 'mr', 'gu', 'kn', 'ne', 'si', 'ps', 'fa',
  'tr', 'ku', 'sw', 'am', 'so', 'ha', 'yo', 'ig', 'zu', 'xh', 'af', 'rw', 'om', 'ti', 'pt', 'es', 'de', 'ru', 'zh', 'ms', 'id', 'tl',
  'vi', 'th', 'ko', 'ja', 'it', 'nl', 'pl', 'uk', 'he'];

export function languageName(code: string | null | undefined, lang: Lang): string {
  if (!code) return '';
  try { return names(lang, 'language')?.of(code) ?? code; } catch { return code; }
}

export function languageOptions(lang: Lang): Array<{ code: string; name: string }> {
  const collator = new Intl.Collator(lang);
  return LANGUAGE_CODES.map(code => ({ code, name: languageName(code, lang) })).sort((a, b) => collator.compare(a.name, b.name));
}

/* only http(s) links from the server are ever made clickable */
export function safeHttpUrl(href: string): string | null {
  try {
    const u = new URL(href);
    return u.protocol === 'https:' || u.protocol === 'http:' ? u.href : null;
  } catch { return null; }
}

/* Where checkout and receipts send the student: https only. The one exception is the local dev server in fake mode
   (npm run dev, the end-to-end tests), whose fake Stripe pages are on http://localhost, the page's own host. */
export function paymentPageUrl(href: string, here: { protocol: string; hostname: string } = window.location): string | null {
  const url = safeHttpUrl(href);
  if (!url) return null;
  if (url.startsWith('https:')) return url;
  const local = here.protocol === 'http:' && (here.hostname === 'localhost' || here.hostname === '127.0.0.1');
  return local && new URL(url).hostname === here.hostname ? url : null;
}
